import { useEffect, useMemo, useRef, useState } from 'react'
import { PRODUCTS, productLines, remainingProducts } from './constants'

/**
 * ⌘K. The mockup's palette only created things, which made it a weaker copy of
 * the sidebar tree — so this one also navigates: type a station or a product
 * and go straight there, or pick one of the create actions.
 */
export default function CommandPalette({ open, stations, onClose, onNavigate, onCreate }) {
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const inputRef = useRef(null)
  const listRef = useRef(null)

  useEffect(() => {
    if (open) { setQuery(''); setCursor(0); inputRef.current?.focus() }
  }, [open])

  const items = useMemo(() => {
    const out = []
    out.push({ id: 'nav-org', group: 'Go to', label: 'All stations', run: () => onNavigate({ view: 'org', stationId: null }) })

    for (const s of stations) {
      out.push({ id: `nav-${s.id}`, group: 'Go to', label: s.name, hint: s.location || undefined,
        run: () => onNavigate({ view: 'station', stationId: s.id }) })
      for (const line of productLines(s)) {
        out.push({
          id: `nav-${s.id}-${line.product}`, group: 'Go to',
          label: `${s.name} · ${line.product}`, colour: PRODUCTS[line.product]?.color,
          hint: `${line.tanks.length} tank${line.tanks.length === 1 ? '' : 's'}, ${line.pumps.length} pump${line.pumps.length === 1 ? '' : 's'}`,
          run: () => onNavigate({ view: 'station', stationId: s.id }),
        })
      }
    }

    out.push({ id: 'new-station', group: 'Create', label: 'Add a station', run: () => onCreate({ kind: 'newStation' }) })
    for (const s of stations) {
      if (remainingProducts(s).length > 0) {
        out.push({ id: `new-line-${s.id}`, group: 'Create', label: `Add a product to ${s.name}`,
          run: () => onCreate({ kind: 'newLine', stationId: s.id }) })
      }
      for (const line of productLines(s)) {
        out.push({ id: `new-tank-${s.id}-${line.product}`, group: 'Create',
          label: `Add a tank to ${s.name} · ${line.product}`, colour: PRODUCTS[line.product]?.color,
          run: () => onCreate({ kind: 'newTank', stationId: s.id, product: line.product }) })
        out.push({ id: `new-pump-${s.id}-${line.product}`, group: 'Create',
          label: `Add a pump to ${s.name} · ${line.product}`, colour: PRODUCTS[line.product]?.color,
          run: () => onCreate({ kind: 'newPump', stationId: s.id, product: line.product }) })
      }
    }
    return out
  }, [stations, onNavigate, onCreate])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items.slice(0, 12)
    return items.filter((i) => i.label.toLowerCase().includes(q)).slice(0, 20)
  }, [items, query])

  useEffect(() => { setCursor(0) }, [query])

  useEffect(() => {
    if (!open) return undefined
    function onKey(e) {
      if (e.key === 'Escape') { onClose(); return }
      if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(filtered.length - 1, c + 1)) }
      if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)) }
      if (e.key === 'Enter') {
        e.preventDefault()
        const item = filtered[cursor]
        if (item) { item.run(); onClose() }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, filtered, cursor, onClose])

  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [cursor])

  if (!open) return null

  let lastGroup = null

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center pt-[14vh]">
      <div className="absolute inset-0 bg-[#060c0b]/50" onClick={onClose} />
      <div className="relative w-[min(520px,90vw)] overflow-hidden rounded-[22px] border border-gray-200 bg-white shadow-2xl">
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Go to a station, or add a station, tank or pump…"
          aria-label="Search stations and actions"
          className="w-full border-0 border-b border-gray-200 px-5 py-4 text-[15px] outline-none"
        />
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto p-2">
          {filtered.length === 0 && (
            <p className="px-3 py-3 text-sm text-gray-400">Nothing matches &ldquo;{query.trim()}&rdquo;.</p>
          )}
          {filtered.map((item, i) => {
            const header = item.group !== lastGroup ? item.group : null
            lastGroup = item.group
            return (
              <div key={item.id}>
                {header && (
                  <p className="px-3 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[.09em] text-gray-400">{header}</p>
                )}
                <button
                  type="button"
                  data-active={i === cursor}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => { item.run(); onClose() }}
                  className={`flex w-full items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-left text-[13.5px] transition ${
                    i === cursor ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                  }`}
                >
                  {item.group === 'Create'
                    ? <span className="font-mono font-bold text-[#e2932b]">+</span>
                    : <span className="h-2 w-2 flex-shrink-0 rounded-[2px]" style={{ background: item.colour || '#cbd5e1' }} />}
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.hint && <span className="truncate text-[11.5px] text-gray-400">{item.hint}</span>}
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
